import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CalendarClock, ArrowRight, Sparkles } from "lucide-react";
import api from "../lib/api";

/**
 * Shows a banner at the top of the dashboard when the licence holder is within 30 days
 * of renewal. Surfaces the trailing-year usage stats and a one-click renew CTA.
 */
export default function RenewalBanner() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api
      .get("/renewal/usage")
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, []);

  if (!data || !data.show_renewal_prompt) return null;

  const stats = [
    { label: "Audits this year", value: data.audits_completed },
    { label: "Avg score", value: data.average_score },
    { label: "Healthy (Performing + Elite)", value: data.healthy_count },
    { label: "At-risk (Critical + Exposed)", value: data.at_risk_count },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45 }}
      className="relative overflow-hidden rounded-2xl border border-[var(--border-active)] mb-6"
      style={{
        background:
          "linear-gradient(135deg, rgba(0,0,128,0.55), rgba(37,99,235,0.35) 60%, rgba(0,191,255,0.18))",
      }}
      data-testid="renewal-banner"
    >
      <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />
      <div className="relative p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 items-center">
        <div>
          <div className="flex items-center gap-2 text-brand text-xs uppercase tracking-[0.2em] font-mono">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Renewal Saver · {data.days_until_renewal} days until renewal</span>
          </div>
          <h2 className="font-display text-2xl sm:text-3xl mt-2 leading-tight">
            Look at the year you just had with Fitest.
          </h2>
          <p className="text-slate-300 text-sm mt-2 max-w-2xl">
            Renew your <span className="text-white">{data.license_type === "business" ? "Business" : "Gym"} Annual Unlimited Licence</span>{" "}
            to keep collecting audits, dashboards, and {data.license_type === "business" ? "departmental" : "member retention"} insights.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
            {stats.map((s) => (
              <div key={s.label} className="rounded-xl px-3 py-2 bg-black/30 border border-white/10" data-testid={`renewal-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">{s.label}</div>
                <div className="font-display text-2xl text-white mt-0.5">{s.value}</div>
              </div>
            ))}
          </div>
        </div>

        <a href={data.renew_url} target="_blank" rel="noopener noreferrer" className="self-stretch lg:self-center" data-testid="renewal-cta">
          <button className="btn-glow rounded-full h-12 px-7 inline-flex items-center w-full lg:w-auto justify-center">
            <CalendarClock className="w-4 h-4 mr-2" />
            Renew now
            <ArrowRight className="w-4 h-4 ml-1" />
          </button>
        </a>
      </div>
    </motion.div>
  );
}
